import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { money } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Plus, Pencil, Trash2, X, Sigma, RotateCcw, History } from "lucide-react";
import UnifiedCatalog from "@/components/catalog/UnifiedCatalog";
import CatalogFees from "@/components/catalog/CatalogFees";
import CommodityTracker from "@/components/catalog/CommodityTracker";

const prettify = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

function AssembliesTab({ canEdit }) {
  const [items, setItems] = useState([]);
  const load = () => api.get("/assemblies").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Delete this assembly?")) return;
    try { await api.delete(`/assemblies/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };
  return (
    <Card className="p-4" data-testid="assemblies-catalog">
      <p className="text-xs text-slate-500 mb-3">Reusable scope assemblies — save a set of estimate lines once on a bid and drop them into any future estimate. Build & save assemblies from a bid's Estimate grid; manage them here.</p>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 text-center">No assemblies saved yet. Save one from a bid's Estimate grid.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((a) => {
            const n = (a.lines || a.scope_lines || []).length;
            return (
              <div key={a.assembly_id} className="flex items-center justify-between py-2" data-testid={`assembly-row-${a.assembly_id}`}>
                <div><div className="text-sm font-medium text-slate-800">{a.name}</div>
                  <div className="text-xs text-slate-400">{n} line{n === 1 ? "" : "s"}{a.discipline ? ` · ${a.discipline}` : ""}</div></div>
                {canEdit && <button onClick={() => del(a.assembly_id)} className="text-slate-300 hover:text-rose-600" data-testid={`assembly-del-${a.assembly_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function EquipmentCatalog({ canEdit }) {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const BLANK = { name: "", category: "", fuel_factor: 0.04, taxable: true };
  const load = () => api.get("/equipment-catalog").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const del = async (id) => {
    if (!window.confirm("Remove this equipment from the catalog?")) return;
    try { await api.delete(`/equipment-catalog/${id}`); toast.success("Removed"); load(); }
    catch { toast.error("Could not remove"); }
  };
  const save = async () => {
    if (!edit?.name?.trim()) { toast.error("Name the equipment first"); return; }
    try { await api.post("/equipment-catalog", edit); toast.success("Saved to catalog"); setEdit(null); load(); }
    catch { toast.error("Could not save"); }
  };
  const FIELDS = [
    ["Ownership (fixed)", [["purchase_price", "Purchase $"], ["salvage_value", "Salvage $"], ["useful_life_years", "Life (yrs)"], ["annual_hours", "Annual hrs"], ["interest_pct", "Interest %/yr"], ["insurance_pct", "Insurance %/yr"], ["storage_annual", "Storage $/yr"]]],
    ["Operating (fuel uses the site diesel price)", [["horsepower", "HP"], ["load_factor", "Load factor"], ["fuel_factor", "Fuel gal/hp-hr"], ["fog_pct", "FOG % of fuel"], ["repairs_hourly", "Repairs $/hr"], ["tires_set_cost", "Tires set $"], ["tires_life_hours", "Tires life hrs"], ["environmental_hourly", "Enviro $/hr"]]],
    ["Standards", [["delivery_cost", "Delivery / mob $"]]],
  ];
  return (
    <Card className="p-4" data-testid="equipment-catalog">
      <div className="flex items-start justify-between gap-3 mb-3">
        <p className="text-xs text-slate-500 flex-1">Reusable equipment with full USACE-style cost build-ups (ownership + operating). <b>Fuel and sales tax resolve from each project's location</b> automatically — everything else is a stored standard. Add equipment here or from a bid's scope <b>Equipment</b> panel.</p>
        {canEdit && <Button size="sm" onClick={() => setEdit({ ...BLANK })} className="bg-blue-600 hover:bg-blue-700 shrink-0" data-testid="add-equipment"><Plus className="w-4 h-4 mr-1" /> Add equipment</Button>}
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-400 italic py-6 text-center">No equipment saved yet. Click “Add equipment” to build your first item.</div>
      ) : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Equipment</TableHead><TableHead>Category</TableHead>
            <TableHead className="text-right">HP</TableHead><TableHead className="text-right">Purchase</TableHead>
            <TableHead className="text-right">Annual hrs</TableHead><TableHead className="text-right">Delivery</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {items.map((e) => (
              <TableRow key={e.equipment_id} data-testid={`equip-catalog-row-${e.equipment_id}`}>
                <TableCell className="font-medium">{canEdit ? <button onClick={() => setEdit({ ...e })} className="text-[#003E87] hover:underline text-left" data-testid={`equip-edit-open-${e.equipment_id}`}>{e.name}</button> : e.name}</TableCell>
                <TableCell className="text-slate-500">{e.category || "—"}</TableCell>
                <TableCell className="text-right font-mono">{e.horsepower || "—"}</TableCell>
                <TableCell className="text-right font-mono">{e.purchase_price ? money(e.purchase_price, 0) : "—"}</TableCell>
                <TableCell className="text-right font-mono">{e.annual_hours || "—"}</TableCell>
                <TableCell className="text-right font-mono">{e.delivery_cost ? money(e.delivery_cost, 0) : "—"}</TableCell>
                <TableCell className="text-right">{canEdit && <button onClick={() => del(e.equipment_id)} className="text-slate-300 hover:text-rose-600" data-testid={`equip-catalog-del-${e.equipment_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {edit && (
        <Dialog open onOpenChange={(o) => !o && setEdit(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="equipment-editor">
            <DialogHeader><DialogTitle>{edit.equipment_id ? "Edit equipment" : "Add equipment"}</DialogTitle>
              <DialogDescription>Set the standard cost build-up once. Fuel and sales tax come from each project's location at compute time.</DialogDescription></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-slate-500">Name</span><Input value={edit.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="160 HP Excavator" data-testid="equip-edit-name" /></label>
                <label className="block"><span className="text-xs text-slate-500">Category</span><Input value={edit.category || ""} onChange={(e) => setEdit({ ...edit, category: e.target.value })} placeholder="Earthwork, Cranes, Piling…" data-testid="equip-edit-category" /></label>
              </div>
              {FIELDS.map(([sec, fields]) => (
                <div key={sec}>
                  <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{sec}</div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {fields.map(([k, lbl]) => (
                      <label key={k} className="block"><span className="text-[10px] text-slate-400">{lbl}</span>
                        <Input type="number" step="any" value={edit[k] ?? ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`equip-edit-${k}`} className="text-right font-mono" placeholder="0" /></label>
                    ))}
                  </div>
                </div>
              ))}
              <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" checked={edit.taxable !== false} onChange={(e) => setEdit({ ...edit, taxable: e.target.checked })} data-testid="equip-edit-taxable" /> Taxable (site sales tax applies)</label>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="equip-edit-save">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
}

function CostCodes({ canEdit }) {
  const [data, setData] = useState({ standard: [], custom: [] });
  const [form, setForm] = useState({ code: "", name: "" });
  const load = () => api.get("/cost-codes").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!form.code.trim() || !form.name.trim()) { toast.error("Code and name are required."); return; }
    try { await api.post("/cost-codes", form); setForm({ code: "", name: "" }); toast.success("Cost code added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
  };
  const del = async (code) => { try { await api.delete(`/cost-codes/${encodeURIComponent(code)}`); load(); } catch { toast.error("Could not delete"); } };
  return (
    <div className="space-y-4" data-testid="cost-codes-manager">
      <p className="text-xs text-slate-500">Cost codes are workspace-wide and standardized. Only admins (owner / chief estimator) can add or remove them — estimators assign existing codes to lines, assemblies and items but can't invent new ones.</p>
      {canEdit && (
        <Card className="p-3 flex flex-wrap items-end gap-2" data-testid="cost-code-add">
          <div><Label className="text-[11px]">Code</Label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="16-4000" className="h-8 w-32 font-mono" data-testid="cc-code" /></div>
          <div className="flex-1 min-w-[200px]"><Label className="text-[11px]">Name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="DC Collection" className="h-8" data-testid="cc-name" /></div>
          <Button size="sm" onClick={add} className="bg-blue-600 hover:bg-blue-700" data-testid="cc-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </Card>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Standard (CSI-based)</div>
          <div className="space-y-1">{data.standard.map((c) => (<div key={c.code} className="flex justify-between text-xs text-slate-600" data-testid={`cc-std-${c.code}`}><span className="font-mono text-slate-500">{c.code}</span><span className="truncate ml-2">{c.name}</span></div>))}</div>
        </Card>
        <Card className="p-4">
          <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Custom ({data.custom.length})</div>
          {data.custom.length === 0 && <div className="text-xs text-slate-400">None yet.</div>}
          <div className="space-y-1">{data.custom.map((c) => (<div key={c.code} className="flex justify-between items-center text-xs text-slate-600 group" data-testid={`cc-custom-${c.code}`}><span className="flex-1 min-w-0 flex gap-2"><span className="font-mono text-slate-500">{c.code}</span><span className="truncate">{c.name}</span></span>{canEdit && <button onClick={() => del(c.code)} className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100"><Trash2 className="w-3.5 h-3.5" /></button>}</div>))}</div>
        </Card>
      </div>
    </div>
  );
}

function Conformance({ active, roleOptions, canEdit, onReload }) {
  const conf = active?.conformance || {};
  const entries = Object.entries(conf);
  const [form, setForm] = useState({ role: "", mode: "alias", alias: "", base: "", fringe: "" });

  const save = async (role, mapping) => {
    try {
      await api.put("/rate-libraries/conformance", { conformance: { [role]: mapping } });
      toast.success(`Conformance saved for “${role}”`);
      setForm({ role: "", mode: "alias", alias: "", base: "", fringe: "" });
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const remove = async (role) => {
    try { await api.put("/rate-libraries/conformance", { remove: [role] }); toast.success("Removed"); onReload(); }
    catch { toast.error("Could not remove"); }
  };
  const addForm = () => {
    if (!form.role.trim()) return toast.error("Enter the classification name.");
    if (form.mode === "alias") {
      if (!form.alias) return toast.error("Pick a role to conform to.");
      save(form.role.trim(), { alias: form.alias });
    } else {
      if (form.base === "") return toast.error("Enter a base rate.");
      save(form.role.trim(), { base: Number(form.base), fringe: Number(form.fringe || 0) });
    }
  };
  const describe = (m) => m.alias ? `→ uses rate of “${m.alias}”` : `→ $${Number(m.base || 0).toFixed(2)} base + $${Number(m.fringe || 0).toFixed(2)} fringe = $${(Number(m.base || 0) + Number(m.fringe || 0)).toFixed(2)}/hr`;

  return (
    <div className="space-y-4" data-testid="conformance-manager">
      <p className="text-xs text-slate-500">Classification conformance maps a wage classification that isn't in the active rate library onto a real rate — either by reusing an existing role's rate, or with a manual base + fringe. Imports use these automatically so a missing classification never silently prices at $0.</p>
      {!active && <div className="text-sm text-amber-600">No active rate library. Activate one first.</div>}
      {active && canEdit && (
        <Card className="p-3 flex flex-wrap items-end gap-2" data-testid="conf-add">
          <div className="flex-1 min-w-[220px]"><Label className="text-[11px]">Classification</Label><Input value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} placeholder="Pile Driver Operator" className="h-8" data-testid="conf-role" /></div>
          <div><Label className="text-[11px]">Conform to</Label>
            <Select value={form.mode} onValueChange={(v) => setForm((f) => ({ ...f, mode: v }))}>
              <SelectTrigger className="h-8 w-40" data-testid="conf-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="alias">Existing role</SelectItem>
                <SelectItem value="manual">Base + fringe</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {form.mode === "alias" ? (
            <div><Label className="text-[11px]">Role</Label>
              <Select value={form.alias} onValueChange={(v) => setForm((f) => ({ ...f, alias: v }))}>
                <SelectTrigger className="h-8 w-48" data-testid="conf-alias"><SelectValue placeholder="Pick role…" /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div><Label className="text-[11px]">Base $/hr</Label><Input value={form.base} onChange={(e) => setForm((f) => ({ ...f, base: e.target.value }))} className="h-8 w-24" data-testid="conf-base" /></div>
              <div><Label className="text-[11px]">Fringe $/hr</Label><Input value={form.fringe} onChange={(e) => setForm((f) => ({ ...f, fringe: e.target.value }))} className="h-8 w-24" data-testid="conf-fringe" /></div>
            </>
          )}
          <Button size="sm" onClick={addForm} className="bg-blue-600 hover:bg-blue-700" data-testid="conf-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </Card>
      )}
      <Card className="p-4">
        <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-2">Saved mappings ({entries.length})</div>
        {entries.length === 0 && <div className="text-xs text-slate-400">No conformances yet.</div>}
        <div className="space-y-1.5">
          {entries.map(([role, m]) => (
            <div key={role} className="flex justify-between items-center text-sm text-slate-600 group border-b border-slate-50 pb-1.5" data-testid={`conf-row-${role}`}>
              <span className="flex-1 min-w-0"><span className="font-medium text-slate-700">{role}</span> <span className="text-xs text-slate-500">{describe(m)}</span></span>
              {canEdit && <button onClick={() => remove(role)} className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100" data-testid={`conf-remove-${role}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}


export default function Libraries() {
  const { user } = useAuth();
  const canEdit = ["owner", "admin", "chief_estimator"].includes(user?.role);
  const [taskTypes, setTaskTypes] = useState([]);
  const [crews, setCrews] = useState([]);
  const [rates, setRates] = useState([]);
  const [clauses, setClauses] = useState([]);
  const [groupCrews, setGroupCrews] = useState([]);
  const [editor, setEditor] = useState(null); // {kind, data}

  const load = async () => {
    const [tt, cr, rl, cl, gc] = await Promise.all([
      api.get("/task-types"), api.get("/crew-templates"), api.get("/rate-libraries"), api.get("/clauses"), api.get("/group-crews"),
    ]);
    setTaskTypes(tt.data); setCrews(cr.data); setRates(rl.data); setClauses(cl.data); setGroupCrews(gc.data || []);
  };
  useEffect(() => { load(); }, []);

  const delGroupCrew = async (id) => { await api.delete(`/group-crews/${id}`); toast.success("Removed from catalog"); setGroupCrews((l) => l.filter((x) => x.id !== id)); };

  const active = rates.find((r) => r.is_active);
  const roleOptions = active?.labor_roles?.map((l) => l.role) || [];
  const equipOptions = active?.equipment?.map((e) => e.name) || [];

  const activate = async (vid) => { await api.post(`/rate-libraries/${vid}/activate`); toast.success("Activated"); load(); };
  const del = async (path, id) => { await api.delete(`${path}/${id}`); toast.success("Deleted"); load(); };
  const saved = () => { setEditor(null); load(); };

  return (
    <div className="max-w-[1800px] w-full mx-auto px-6 sm:px-8 py-8">
      <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Libraries</h1>
      <p className="text-sm text-slate-500 mt-0.5 mb-6">The single source for every number. Nothing here is project-specific — bids use these templates and can override per job.</p>

      <Tabs defaultValue="catalog">
        <TabsList data-testid="libraries-tabs">
          <TabsTrigger value="catalog" data-testid="lib-tab-catalog">Catalog</TabsTrigger>
          <TabsTrigger value="indexes" data-testid="lib-tab-indexes">Indexes</TabsTrigger>
          <TabsTrigger value="crews" data-testid="lib-tab-crews">Crews</TabsTrigger>
          <TabsTrigger value="saved_crews" data-testid="lib-tab-saved-crews">Saved crews</TabsTrigger>
          <TabsTrigger value="cost_codes" data-testid="lib-tab-cost-codes">Cost codes</TabsTrigger>
          <TabsTrigger value="fees" data-testid="lib-tab-fees">Fees</TabsTrigger>
        </TabsList>

        {/* UNIFIED CATALOG (line items + assemblies + equipment) */}
        <TabsContent value="catalog" className="mt-4">
          <UnifiedCatalog canEdit={canEdit} />
        </TabsContent>

        {/* COMMODITY INDEX TRACKER (Yahoo Finance) */}
        <TabsContent value="indexes" className="mt-4">
          <CommodityTracker canEdit={canEdit} />
        </TabsContent>

        {/* CREWS */}
        <TabsContent value="crews" className="mt-4">
          {canEdit && <div className="flex justify-end mb-2"><Button size="sm" onClick={() => setEditor({ kind: "crew", data: { discipline: "mechanical", roles: [{ role: roleOptions[0] || "", count: 1 }] } })} className="bg-blue-600 hover:bg-blue-700" data-testid="add-crew"><Plus className="w-4 h-4 mr-1" /> Add crew</Button></div>}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {crews.map((c) => (
              <Card key={c.id} className="p-4" data-testid={`crew-card-${c.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium">{c.name}</div>
                  <div className="flex items-center gap-2"><Disc d={c.discipline} />
                    {canEdit && <><button onClick={() => setEditor({ kind: "crew", data: { ...c } })} className="text-slate-400 hover:text-blue-600"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del("/crew-templates", c.id)} className="text-slate-300 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button></>}
                  </div>
                </div>
                <div className="mt-2 space-y-1">
                  {c.roles.map((r, i) => (<div key={i} className="flex justify-between text-xs text-slate-600"><span>{r.role}</span><span className="font-mono">×{r.count}</span></div>))}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* COST CODES */}
        <TabsContent value="cost_codes" className="mt-4">
          <CostCodes canEdit={canEdit} />
        </TabsContent>

        {/* SAVED CREWS (reusable group crews built inside bids) */}
        <TabsContent value="saved_crews" className="mt-4">
          <p className="text-sm text-slate-500 mb-3">Reusable crews built on a bid's scope and saved with "Save to library". Load any of these onto a scope from the crew panel to staff a bid in one click.</p>
          {groupCrews.length === 0 ? (
            <Card className="p-8 text-center text-sm text-slate-400" data-testid="saved-crews-empty">No saved crews yet. In a bid's Estimate grid, build a crew on a scope and click "Save to library".</Card>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {groupCrews.map((g) => {
                const members = g.crew?.members || [];
                const hc = members.reduce((s, m) => s + (Number(m.count) || 0), 0);
                return (
                  <Card key={g.id} className="p-4" data-testid={`saved-crew-card-${g.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{g.name}</div>
                      {canEdit && <button onClick={() => delGroupCrew(g.id)} className="text-slate-300 hover:text-rose-600" data-testid={`saved-crew-del-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{hc} crew{g.crew?.production_rate ? ` · ${g.crew.production_rate} ${g.crew.production_unit || "u"}/day` : ""}{g.source?.project_name ? ` · from ${g.source.project_name}` : ""}</div>
                    <div className="mt-2 space-y-1">
                      {members.map((m, i) => (<div key={i} className="flex justify-between text-xs text-slate-600"><span className="truncate">{m.role || "Worker"}</span><span className="font-mono">×{m.count} · ${m.base_rate}+${m.fringe}</span></div>))}
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* GLOBAL CATALOG FEES + EQUIPMENT CATEGORIES */}
        <TabsContent value="fees" className="mt-4">
          <CatalogFees canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      {editor?.kind === "task" && <TaskEditor data={editor.data} crews={crews} equipOptions={equipOptions} onClose={() => setEditor(null)} onSaved={saved} />}
      {editor?.kind === "crew" && <CrewEditor data={editor.data} roleOptions={roleOptions} onClose={() => setEditor(null)} onSaved={saved} />}
      {editor?.kind === "rate" && <RateEditor data={editor.data} onClose={() => setEditor(null)} onSaved={saved} />}
      {editor?.kind === "clause" && <ClauseEditor data={editor.data} onClose={() => setEditor(null)} onSaved={saved} />}
      {editor?.kind === "calc" && <CalculatorEditor task={editor.data} onClose={() => setEditor(null)} onSaved={saved} onReload={load} />}
    </div>
  );
}

const CALC_GROUPS = [
  ["standards", "Production standards", "Field-calibrated drive/cycle times, MH rates, hours/day, fees and rent basis."],
  ["crew_ratios", "Crew ratios", "How the crew auto-sizes — apprentice %, workers per lead, operator ratios."],
  ["site_condition_weights", "Site-condition weights", "How heavily each axis slows THIS scope (0–1). Piles weigh soil highest; modules weigh environmental."],
  ["soil_classes", "Soil penalty (simple)", null],
  ["terrain_classes", "Terrain penalty", null],
  ["access_classes", "Access penalty", null],
  ["environmental_classes", "Environmental penalty", null],
];

function CalcGroup({ title, help, obj, basePath, setVal }) {
  const entries = Object.entries(obj).filter(([, v]) => v === null || typeof v !== "object");
  if (entries.length === 0) return null;
  return (
    <div data-testid={`calc-group-${basePath.join("-")}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium">{title}</div>
      {help && <div className="text-[11px] text-slate-400 mb-1.5">{help}</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-1">
        {entries.map(([k, v]) => (
          <div key={k}>
            <Label className="text-[10px] text-slate-500">{prettify(k)}</Label>
            <Input value={v ?? ""} type={typeof v === "number" ? "number" : "text"} step="any"
              onChange={(e) => setVal([...basePath, k], e.target.value)}
              className="h-8 text-xs font-mono mt-0.5" data-testid={`calc-field-${basePath.join("-")}-${k}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

function CalculatorEditor({ task, onClose, onSaved, onReload }) {
  const [cfg, setCfg] = useState(() => structuredClone(task.calculator || {}));
  const [saving, setSaving] = useState(false);
  const [baseline, setBaseline] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showHist, setShowHist] = useState(false);
  const [history, setHistory] = useState([]);
  const resettable = ["tt_pile", "tt_rack", "tt_module", "tt_dc_elec"].includes(task.id);

  const setVal = (path, raw) => setCfg((prev) => {
    const next = structuredClone(prev);
    let o = next;
    for (let i = 0; i < path.length - 1; i++) { if (!o[path[i]]) o[path[i]] = {}; o = o[path[i]]; }
    const key = path[path.length - 1];
    o[key] = typeof o[key] === "number" ? (raw === "" ? "" : Number(raw)) : raw;
    return next;
  });

  const previewFor = async (candidate) => {
    try { const r = await api.post(`/task-types/${task.id}/calc-preview`, { calculator: candidate }); return r.data; }
    catch { return null; }
  };
  useEffect(() => { previewFor(task.calculator).then(setBaseline); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(() => { previewFor(cfg).then(setPreview); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [cfg]);

  const loadHist = () => api.get(`/task-types/${task.id}/calculator-history`).then((r) => setHistory(r.data)).catch(() => {});
  const toggleHist = () => { const n = !showHist; setShowHist(n); if (n) loadHist(); };

  const save = async () => {
    setSaving(true);
    try { await api.post("/task-types", { ...task, calculator: cfg }); toast.success("Calculator coefficients saved"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Restore this calculator's coefficients to the shipped factory defaults? Your edits will be discarded.")) return;
    try {
      const r = await api.post(`/task-types/${task.id}/reset-calculator`);
      setCfg(structuredClone(r.data.calculator || {}));
      toast.success("Restored factory defaults");
      onReload && onReload(); if (showHist) loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not restore defaults"); }
  };
  const rollback = async (hid) => {
    try {
      const r = await api.post(`/task-types/${task.id}/calculator-history/${hid}/restore`);
      setCfg(structuredClone(r.data.calculator || {}));
      toast.success("Rolled back to that version");
      onReload && onReload(); loadHist();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not roll back"); }
  };

  const soil = cfg.soil_factors;
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-3xl" data-testid="calc-editor">
      <DialogHeader>
        <div className="flex items-center justify-between pr-6">
          <DialogTitle className="flex items-center gap-2"><Sigma className="w-4 h-4 text-indigo-600" /> Calculator — {cfg.label || task.name}</DialogTitle>
          <Button variant="ghost" size="sm" onClick={toggleHist} data-testid="calc-history-toggle" className="text-slate-500 hover:text-indigo-700"><History className="w-3.5 h-3.5 mr-1.5" /> History</Button>
        </div>
        <DialogDescription>Edit the coefficients this scope uses to compute time, crews and cost. The panel below prices a sample scope live so you can see the effect before saving. A bid can still override per job.</DialogDescription>
      </DialogHeader>

      <WhatIfBar preview={preview} baseline={baseline} />

      {showHist && (
        <div className="rounded-md border border-slate-200 bg-slate-50 p-3 max-h-48 overflow-auto" data-testid="calc-history-panel">
          <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Version history</div>
          {history.length === 0 && <div className="text-xs text-slate-400">No saved versions yet — save an edit to start the history.</div>}
          <div className="space-y-1">
            {history.map((h, i) => (
              <div key={h.history_id} className="flex items-center justify-between text-xs bg-white border border-slate-200 rounded px-2 py-1.5" data-testid={`calc-history-row-${i}`}>
                <div className="min-w-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded mr-2 capitalize ${h.action === "reset" ? "bg-amber-100 text-amber-700" : h.action === "rollback" ? "bg-indigo-100 text-indigo-700" : "bg-slate-100 text-slate-600"}`}>{h.action}</span>
                  <span className="text-slate-600">{h.edited_by || "someone"}</span>
                  <span className="text-slate-400"> · {new Date(h.edited_at).toLocaleString()}</span>
                </div>
                {i > 0 && <button onClick={() => rollback(h.history_id)} data-testid={`calc-rollback-${i}`} className="text-indigo-600 hover:underline shrink-0 ml-2 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Roll back</button>}
                {i === 0 && <span className="text-[10px] text-slate-400 shrink-0 ml-2">current</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-5 max-h-[52vh] overflow-auto pr-1">
        {CALC_GROUPS.filter(([g]) => cfg[g] && typeof cfg[g] === "object").map(([g, title, help]) => (
          <CalcGroup key={g} title={title} help={help} obj={cfg[g]} basePath={[g]} setVal={setVal} />
        ))}
        {soil && typeof soil === "object" && (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500 font-medium mb-1.5">Soil &amp; subsurface penalties</div>
            <div className="space-y-3">
              {Object.keys(soil).map((axis) => (
                <CalcGroup key={axis} title={prettify(axis)} obj={soil[axis]} basePath={["soil_factors", axis]} setVal={setVal} />
              ))}
            </div>
          </div>
        )}
      </div>
      <DialogFooter className="sm:justify-between">
        {resettable ? <Button variant="ghost" onClick={reset} className="text-slate-500 hover:text-rose-600" data-testid="calc-reset"><RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Restore defaults</Button> : <span />}
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="calc-save">Save coefficients</Button>
        </div>
      </DialogFooter>
    </DialogContent></Dialog>
  );
}

function WhatIfBar({ preview, baseline }) {
  if (!preview) return <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-400" data-testid="calc-whatif">Pricing a sample scope…</div>;
  if (preview.status !== "priced") {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800" data-testid="calc-whatif">
        Sample scope can't be priced yet{preview.warnings?.[0] ? ` — ${preview.warnings[0]}` : ". Check that the active rate library has the needed labor roles."}
      </div>
    );
  }
  const dDays = baseline?.status === "priced" ? (preview.work_days - baseline.work_days) : 0;
  const dCost = baseline?.status === "priced" ? (preview.subtotal - baseline.subtotal) : 0;
  const Delta = ({ v, money: m }) => {
    if (!v) return <span className="text-slate-400 ml-1">(no change)</span>;
    const up = v > 0;
    return <span className={`ml-1 font-medium ${up ? "text-rose-600" : "text-emerald-600"}`}>{up ? "▲" : "▼"} {m ? money(Math.abs(v)) : Math.abs(v)}</span>;
  };
  return (
    <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3" data-testid="calc-whatif">
      <div className="text-[11px] uppercase tracking-wide text-indigo-700 font-medium mb-1.5">Live what-if — sample: {preview.sample_label}</div>
      <div className="grid grid-cols-3 gap-3">
        <div><div className="text-[10px] uppercase text-slate-400">Work days</div><div className="font-mono text-sm text-slate-900" data-testid="whatif-days">{preview.work_days ?? "—"}<Delta v={dDays} /></div></div>
        <div><div className="text-[10px] uppercase text-slate-400">Crew</div><div className="font-mono text-sm text-slate-900" data-testid="whatif-crew">{preview.headcount ?? "—"}</div></div>
        <div><div className="text-[10px] uppercase text-slate-400">Sample line cost</div><div className="font-mono text-sm text-slate-900" data-testid="whatif-cost">{money(preview.subtotal)}<Delta v={dCost} money /></div></div>
      </div>
      <div className="text-[10px] text-slate-400 mt-1">Δ compared to the last-saved version. Combined slowdown ×{preview.combined_modifier ?? "—"}.</div>
    </div>
  );
}

function Disc({ d }) {
  return <span className={`text-[11px] px-2 py-0.5 rounded capitalize ${d === "electrical" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"}`}>{d}</span>;
}

function TaskEditor({ data, crews, equipOptions, onClose, onSaved }) {
  const [f, setF] = useState({ ...data, default_equipment: (data.default_equipment || []).join(", ") });
  const save = async () => {
    if (!f.name?.trim()) { toast.error("Name required"); return; }
    const body = { ...f, production_rate: Number(f.production_rate), material_unit_cost: f.material_unit_cost === "" || f.material_unit_cost == null ? 0 : Number(f.material_unit_cost),
      default_equipment: String(f.default_equipment || "").split(",").map((s) => s.trim()).filter(Boolean) };
    await api.post("/task-types", body); toast.success("Task type saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="task-editor">
      <DialogHeader><DialogTitle>{data.id ? "Edit" : "New"} task type</DialogTitle><DialogDescription>A template: production rate, default crew, equipment and unit. Bids can override per job.</DialogDescription></DialogHeader>
      <div className="space-y-3 max-h-[60vh] overflow-auto pr-1">
        <div><Label className="text-xs">Name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="task-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Discipline</Label><Select value={f.discipline} onValueChange={(v) => setF({ ...f, discipline: v })}><SelectTrigger data-testid="task-discipline"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mechanical">Mechanical</SelectItem><SelectItem value="electrical">Electrical</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Category</Label><Input value={f.category || ""} onChange={(e) => setF({ ...f, category: e.target.value })} /></div>
          <div><Label className="text-xs">Unit</Label><Input value={f.unit || ""} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="task-unit" /></div>
          <div><Label className="text-xs">Production / crew-day</Label><Input type="number" value={f.production_rate ?? ""} onChange={(e) => setF({ ...f, production_rate: e.target.value })} data-testid="task-rate" /></div>
          <div><Label className="text-xs">Material / unit ($)</Label><Input type="number" value={f.material_unit_cost ?? ""} onChange={(e) => setF({ ...f, material_unit_cost: e.target.value })} /></div>
          <div><Label className="text-xs">Default crew</Label><Select value={f.default_crew_template_id || ""} onValueChange={(v) => setF({ ...f, default_crew_template_id: v })}><SelectTrigger data-testid="task-crew"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{crews.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select></div>
        </div>
        <div><Label className="text-xs">Default equipment (comma-separated)</Label><Input value={f.default_equipment} onChange={(e) => setF({ ...f, default_equipment: e.target.value })} placeholder={equipOptions.slice(0, 3).join(", ")} /></div>
        <div><Label className="text-xs">Description</Label><Textarea value={f.description || ""} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="task-save">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function CrewEditor({ data, roleOptions, onClose, onSaved }) {
  const [f, setF] = useState({ ...data, roles: data.roles ? [...data.roles] : [] });
  const setRole = (i, k, v) => setF({ ...f, roles: f.roles.map((r, idx) => idx === i ? { ...r, [k]: k === "count" ? Number(v) : v } : r) });
  const save = async () => {
    if (!f.name?.trim()) { toast.error("Name required"); return; }
    await api.post("/crew-templates", { ...f, roles: f.roles.filter((r) => r.role) }); toast.success("Crew saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="crew-editor">
      <DialogHeader><DialogTitle>{data.id ? "Edit" : "New"} crew</DialogTitle><DialogDescription>Roles and counts, not names. Hourly cost comes from the active rate library.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={f.name || ""} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="crew-name" /></div>
          <div><Label className="text-xs">Discipline</Label><Select value={f.discipline} onValueChange={(v) => setF({ ...f, discipline: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="mechanical">Mechanical</SelectItem><SelectItem value="electrical">Electrical</SelectItem></SelectContent></Select></div>
        </div>
        <div><Label className="text-xs">Roles</Label>
          <div className="space-y-2 mt-1">
            {f.roles.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={r.role} onValueChange={(v) => setRole(i, "role", v)}><SelectTrigger className="flex-1 h-9" data-testid={`crew-role-${i}`}><SelectValue placeholder="Role" /></SelectTrigger><SelectContent>{roleOptions.map((ro) => <SelectItem key={ro} value={ro}>{ro}</SelectItem>)}</SelectContent></Select>
                <Input type="number" value={r.count} onChange={(e) => setRole(i, "count", e.target.value)} className="w-20 h-9" />
                <button onClick={() => setF({ ...f, roles: f.roles.filter((_, idx) => idx !== i) })} className="text-slate-300 hover:text-rose-600"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setF({ ...f, roles: [...f.roles, { role: roleOptions[0] || "", count: 1 }] })} data-testid="crew-add-role"><Plus className="w-3.5 h-3.5 mr-1" /> Add role</Button>
          </div>
        </div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="crew-save">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function RateEditor({ data, onClose, onSaved }) {
  const [f, setF] = useState({ ...data, effective_date: data.effective_date || new Date().toISOString().slice(0, 10),
    region: data.region || "National", name: data.name || "New rate library",
    labor_roles: data.labor_roles ? [...data.labor_roles] : [], equipment: data.equipment ? [...data.equipment] : [] });
  const setLR = (i, k, v) => setF({ ...f, labor_roles: f.labor_roles.map((r, idx) => idx === i ? { ...r, [k]: k === "hourly_rate" ? Number(v) : v } : r) });
  const setEQ = (i, k, v) => setF({ ...f, equipment: f.equipment.map((r, idx) => idx === i ? { ...r, [k]: k === "daily_rate" ? Number(v) : v } : r) });
  const save = async () => {
    if (!f.name?.trim()) { toast.error("Name required"); return; }
    await api.post("/rate-libraries", { ...f, hours_per_day: Number(f.hours_per_day), per_diem_rate: Number(f.per_diem_rate) });
    toast.success("Rate library saved"); onSaved();
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-2xl" data-testid="rate-editor">
      <DialogHeader><DialogTitle>{data.version_id ? "Edit" : "New"} rate library</DialogTitle><DialogDescription>A dated, regional version. Bids record which version they used. Set active to price with it.</DialogDescription></DialogHeader>
      <div className="space-y-3 max-h-[62vh] overflow-auto pr-1">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="rate-name" /></div>
          <div><Label className="text-xs">Region</Label><Input value={f.region} onChange={(e) => setF({ ...f, region: e.target.value })} /></div>
          <div><Label className="text-xs">Effective date</Label><Input type="date" value={f.effective_date} onChange={(e) => setF({ ...f, effective_date: e.target.value })} /></div>
          <div><Label className="text-xs">Hours / day</Label><Input type="number" value={f.hours_per_day} onChange={(e) => setF({ ...f, hours_per_day: e.target.value })} /></div>
          <div><Label className="text-xs">Per-diem ($/day)</Label><Input type="number" value={f.per_diem_rate} onChange={(e) => setF({ ...f, per_diem_rate: e.target.value })} /></div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center justify-between mb-1"><Label className="text-xs">Labor roles</Label>
              <button onClick={() => setF({ ...f, labor_roles: [...f.labor_roles, { role: "New role", hourly_rate: 50 }] })} className="text-blue-600 text-xs">+ add</button></div>
            <div className="space-y-1">{f.labor_roles.map((l, i) => (<div key={i} className="flex items-center gap-1"><Input value={l.role} onChange={(e) => setLR(i, "role", e.target.value)} className="h-8 text-xs flex-1" /><Input type="number" value={l.hourly_rate} onChange={(e) => setLR(i, "hourly_rate", e.target.value)} className="h-8 text-xs w-20" /><button onClick={() => setF({ ...f, labor_roles: f.labor_roles.filter((_, idx) => idx !== i) })} className="text-slate-300 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button></div>))}</div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1"><Label className="text-xs">Equipment</Label>
              <button onClick={() => setF({ ...f, equipment: [...f.equipment, { name: "New equipment", daily_rate: 500 }] })} className="text-blue-600 text-xs">+ add</button></div>
            <div className="space-y-1">{f.equipment.map((eq, i) => (<div key={i} className="flex items-center gap-1"><Input value={eq.name} onChange={(e) => setEQ(i, "name", e.target.value)} className="h-8 text-xs flex-1" /><Input type="number" value={eq.daily_rate} onChange={(e) => setEQ(i, "daily_rate", e.target.value)} className="h-8 text-xs w-20" /><button onClick={() => setF({ ...f, equipment: f.equipment.filter((_, idx) => idx !== i) })} className="text-slate-300 hover:text-rose-600"><X className="w-3.5 h-3.5" /></button></div>))}</div>
          </div>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={!!f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} data-testid="rate-set-active" /> Make this the active library</label>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="rate-save">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}

function ClauseEditor({ data, onClose, onSaved }) {
  const [f, setF] = useState({ ...data });
  const save = async () => { if (!f.text?.trim()) { toast.error("Text required"); return; } await api.post("/clauses", f); toast.success("Clause saved"); onSaved(); };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent data-testid="clause-editor">
      <DialogHeader><DialogTitle>{data.id ? "Edit" : "New"} clause</DialogTitle><DialogDescription>Clauses are auto-selected into a proposal by discipline. Nothing is written in code.</DialogDescription></DialogHeader>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Kind</Label><Select value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })}><SelectTrigger data-testid="clause-kind"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="inclusion">Inclusion</SelectItem><SelectItem value="exclusion">Exclusion</SelectItem><SelectItem value="clarification">Clarification</SelectItem><SelectItem value="assumption">Assumption</SelectItem></SelectContent></Select></div>
          <div><Label className="text-xs">Applies to</Label><Select value={f.applies_to} onValueChange={(v) => setF({ ...f, applies_to: v })}><SelectTrigger data-testid="clause-applies"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="any">Any</SelectItem><SelectItem value="mechanical">Mechanical</SelectItem><SelectItem value="electrical">Electrical</SelectItem></SelectContent></Select></div>
        </div>
        <div><Label className="text-xs">Text</Label><Textarea value={f.text || ""} onChange={(e) => setF({ ...f, text: e.target.value })} rows={3} data-testid="clause-text" /></div>
      </div>
      <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} className="bg-blue-600 hover:bg-blue-700" data-testid="clause-save">Save</Button></DialogFooter>
    </DialogContent></Dialog>
  );
}
